import React, { createContext, useContext, useState, useEffect } from 'react';

const RestaurantContext = createContext();

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within RestaurantProvider');
  }
  return context;
};

export const RestaurantProvider = ({ children }) => {
  const [restaurantId, setRestaurantId] = useState(() => {
    return localStorage.getItem('restaurantId') || null;
  });
  const [restaurantData, setRestaurantData] = useState(() => {
    const stored = localStorage.getItem('selectedRestaurant');
    return stored ? JSON.parse(stored) : null;
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen for storage changes (for cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'restaurantId') {
        setRestaurantId(e.newValue);
        if (e.newValue) {
          const storedData = localStorage.getItem('selectedRestaurant');
          if (storedData) {
            setRestaurantData(JSON.parse(storedData));
          }
          // Trigger refresh on all components
          setRefreshTrigger(prev => prev + 1);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom restaurant changed event
    const handleRestaurantChanged = (event) => {
      setRestaurantId(event.detail.id);
      setRestaurantData(event.detail);
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('restaurantChanged', handleRestaurantChanged);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('restaurantChanged', handleRestaurantChanged);
    };
  }, []);

  const changeRestaurant = (id, data) => {
    setRestaurantId(id);
    setRestaurantData(data);
    setRefreshTrigger(prev => prev + 1);
    localStorage.setItem('restaurantId', id);
    localStorage.setItem('selectedRestaurant', JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('restaurantChanged', { detail: data }));
  };

  const refresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <RestaurantContext.Provider value={{
      restaurantId,
      restaurantData,
      refreshTrigger,
      changeRestaurant,
      refresh
    }}>
      {children}
    </RestaurantContext.Provider>
  );
};